import { useRef, useState, useEffect } from "react";
import toast from "react-hot-toast";
import Styles from "./LocationInput.module.css";
import { DEFAULT_LOCATION, MapAPIKey } from "../../Utility/Utility";
import axios from "axios";
import { usePlacesAutocomplete } from "places-autocomplete-hook";

export default function LocationInput({
  errorText,
  onSelectAddress,
  defaultAddress = "",
}) {
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const [loadingLocation, setLoadingLocation] = useState(false);

  // UI State to explicitly control dropdown visibility
  const [showSuggestions, setShowSuggestions] = useState(false);

  const {
    value,
    suggestions,
    setValue,
    clearSuggestions,
    loading: placesLoading,
    error: placesError,
    getPlaceDetails,
    handlePlaceSelect,
  } = usePlacesAutocomplete({
    apiKey: MapAPIKey,
    debounce: 500,
    includedRegionCodes: ["IN"],
  });

  // Handle default address initialization
  useEffect(() => {
    setValue(defaultAddress, false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultAddress]);

  // FIX: Click outside listener safely closes suggestions WITHOUT clearing the input value
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
        // Removed clearSuggestions() here so the input text stays intact
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Reverse geocode: lat/lng -> address
  const getAddressFromLatLng = async (lat, lng) => {
    if (!window.google) throw new Error("Google Maps not loaded");

    const geocoder = new window.google.maps.Geocoder();
    return new Promise((resolve, reject) => {
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === "OK" && results && results.length > 0) {
          resolve(results[0].formatted_address);
        } else {
          reject(`Geocoder failed due to: ${status}`);
        }
      });
    });
  };

  // Forward geocode: address -> lat/lng
  const getLatLngFromAddress = async (address) => {
    try {
      const response = await axios.get(
        "https://maps.googleapis.com/maps/api/geocode/json",
        {
          params: {
            address,
            key: MapAPIKey,
          },
        },
      );
      const results = response.data.results;
      if (results && results.length > 0) {
        return results[0].geometry.location;
      } else {
        throw new Error("Address not found");
      }
    } catch (err) {
      console.error("Geocoding error:", err);
      throw new Error(err.message || "Geocoding failed");
    }
  };

  // Get current device location
  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject("Unable to fetch location");
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => {
          toast(error.message, { type: "error" });
          reject(error);
        },
      );
    });
  };

  // Handle "Use current location" button
  const handleCurrentLocation = async () => {
    try {
      setLoadingLocation(true);
      setShowSuggestions(false);
      clearSuggestions();

      const position = await getCurrentLocation();
      const address = await getAddressFromLatLng(position.lat, position.lng);

      setValue(address, false);

      onSelectAddress({
        address,
        ...position,
      });
    } catch (err) {
      toast(err.message, { type: "error" });
    } finally {
      setLoadingLocation(false);
    }
  };

  // Handle user clicking a place suggestion
  const handleSelect = async (placeId, description) => {
    try {
      setLoadingLocation(true);
      setShowSuggestions(false);

      await handlePlaceSelect(placeId);
      clearSuggestions();

      const details = await getPlaceDetails(placeId);
      const finalAddress = details?.formattedAddress || description;

      setValue(finalAddress, false);

      let lat, lng;

      if (details?.location?.latitude !== undefined) {
        lat = details.location.latitude;
        lng = details.location.longitude;
      } else if (typeof details?.location?.lat === "function") {
        lat = details.location.lat();
        lng = details.location.lng();
      } else if (details?.location?.lat !== undefined) {
        lat = details.location.lat;
        lng = details.location.lng;
      } else if (typeof details?.geometry?.location?.lat === "function") {
        lat = details.geometry.location.lat();
        lng = details.geometry.location.lng();
      } else if (details?.geometry?.location?.lat !== undefined) {
        lat = details.geometry.location.lat;
        lng = details.geometry.location.lng;
      } else {
        const latLng = await getLatLngFromAddress(finalAddress);
        lat = latLng.lat;
        lng = latLng.lng;
      }

      onSelectAddress({
        address: finalAddress,
        lat,
        lng,
      });
    } catch (error) {
      console.error("Error fetching place details:", error);
      toast(error.message || "Failed to get location details", {
        type: "error",
      });
    } finally {
      setLoadingLocation(false);
    }
  };

  // Handle manual input (user types address and presses "Go" or Enter)
  const handleManualAddress = async (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      const typedAddress = value;
      if (!typedAddress) return;

      try {
        setLoadingLocation(true);
        setShowSuggestions(false);
        clearSuggestions();

        const latLng = await getLatLngFromAddress(typedAddress);
        onSelectAddress({
          address: typedAddress,
          lat: latLng.lat,
          lng: latLng.lng,
        });
      } catch (err) {
        toast(err.message || "Failed to get location", { type: "error" });
      } finally {
        setLoadingLocation(false);
      }
    }
  };

  const dropdownStyle = {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    borderRadius: "4px",
    boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
    zIndex: 1000,
    maxHeight: "250px",
    overflowY: "auto",
    listStyle: "none",
    padding: 0,
    margin: "4px 0 0 0",
  };

  const listItemStyle = {
    padding: "10px 12px",
    cursor: "pointer",
    borderBottom: "1px solid #f0f0f0",
    fontSize: "14px",
    color: "#333",
    textAlign: "left",
    transition: "background-color 0.2s ease",
  };

  return (
    <div className={Styles.mainContainer}>
      <label className="label">
        Delivery Location <span className="text-danger fs-5">*</span>
      </label>

      <div className={Styles.inputWrapper} ref={wrapperRef}>
        <input
          ref={inputRef}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setShowSuggestions(true);
          }}
          onKeyDown={handleManualAddress}
          placeholder="Select or type address..."
          className={Styles.googleInput}
          autoComplete="off"
        />

        <div className={Styles.liveLocationButtonContainer}>
          <button
            type="button"
            className={Styles.locationBtn}
            onClick={handleCurrentLocation}
            disabled={loadingLocation || placesLoading}
            title="Use current location"
          >
            {loadingLocation ? "⏳" : "📍"}
            <span
              style={{
                fontSize: "10px",
                color: "#666",
                lineHeight: "1",
                fontWeight: "600",
              }}
            >
              Live
            </span>
          </button>
        </div>

        {showSuggestions &&
          suggestions.status === "OK" &&
          suggestions.data &&
          suggestions.data.length > 0 && (
            <ul style={dropdownStyle}>
              {suggestions.data.map((prediction) => {
                const mainText =
                  prediction.structuredFormat?.mainText?.text || "";
                const secondaryText =
                  prediction.structuredFormat?.secondaryText?.text || "";
                const description = secondaryText
                  ? `${mainText}, ${secondaryText}`
                  : mainText;

                return (
                  <li
                    key={prediction.placeId}
                    style={listItemStyle}
                    onClick={() =>
                      handleSelect(prediction.placeId, description)
                    }
                    onMouseEnter={(e) =>
                      (e.target.style.backgroundColor = "#f9f9f9")
                    }
                    onMouseLeave={(e) =>
                      (e.target.style.backgroundColor = "#fff")
                    }
                  >
                    <strong>{mainText}</strong>
                    {secondaryText && (
                      <span style={{ color: "#666" }}>, {secondaryText}</span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
      </div>

      {placesError && (
        <div className={`${Styles.locationErrorMsg} text-danger mt-1`}>
          Failed to load suggestions.
        </div>
      )}

      {errorText && (
        <div className={`${Styles.locationErrorMsg} text-danger`}>
          {errorText}
        </div>
      )}
    </div>
  );
}
